/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/no-multi-comp */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FilterChipBarV2, TableV2, WidgetV2 } from '../../../components/v2';
import DigitacaoSortMenuButton from '../Digitacao/Operador/DigitacaoSortMenuButton';
import {
  EQUIPE_CHIP,
  EQUIPE_CHIP_OPTIONS,
  EQUIPE_SORT,
  EQUIPE_SORT_OPTIONS,
  LENTE_IDS,
  META_DIARIA,
  filterEquipeRows,
  formatAdereLine,
  formatMedia,
  getUnidadeLabel,
  lenteHasAtividade,
  openDigitadorDrill,
  sortEquipeRows,
} from './commandCenterConstants';

const ritmoChipSx = {
  height: 20,
  fontSize: '0.65rem',
  '& .MuiChip-label': { px: 0.75 },
};

const RitmoChip = ({ abaixo }) => (
  <Chip
    label={abaixo ? 'Abaixo' : 'No ritmo'}
    size="small"
    color={abaixo ? 'warning' : 'success'}
    variant="outlined"
    sx={ritmoChipSx}
  />
);

RitmoChip.propTypes = {
  abaixo: PropTypes.bool,
};

RitmoChip.defaultProps = {
  abaixo: false,
};

const LenteGlanceCell = ({ lente, lenteId, showAdere }) => {
  if (!lenteHasAtividade(lente)) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const meta = META_DIARIA[lenteId];
  const unidade = getUnidadeLabel(lenteId);
  const unidadeShort = unidade === 'postes' ? 'postes' : 'serv.';
  const digitados = lente.servicosDigitados ?? 0;
  const totalVol = lente.servicosTotal ?? 0;
  const adereLine = showAdere ? formatAdereLine(lente.adere) : null;
  return (
    <Stack spacing={0.25} sx={{ py: 0.5, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <RitmoChip abaixo={Boolean(lente.abaixoMeta)} />
        <Typography variant="body2" fontWeight={600} noWrap>
          {formatMedia(lente.mediaDiaUtil)}
          {meta != null ? ` / ${meta}` : ''}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap>
        {lente.nsirs ?? 0} NSIRs · {digitados.toLocaleString('pt-BR')}/{totalVol.toLocaleString('pt-BR')} {unidadeShort} · Pend. {(lente.servicosPendentes ?? 0).toLocaleString('pt-BR')}
      </Typography>
      {adereLine ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          {adereLine}
        </Typography>
      ) : null}
    </Stack>
  );
};

LenteGlanceCell.propTypes = {
  lente: PropTypes.object,
  lenteId: PropTypes.string.isRequired,
  showAdere: PropTypes.bool,
};

LenteGlanceCell.defaultProps = {
  lente: null,
  showAdere: false,
};

const EquipeTable = ({ rows, loading }) => {
  const [q, setQ] = useState('');
  const [chip, setChip] = useState(EQUIPE_CHIP.TODOS);
  const [sortId, setSortId] = useState(EQUIPE_SORT.NOME);

  const filtered = useMemo(() => {
    const next = filterEquipeRows(rows, { q, chip });
    return sortEquipeRows(next, sortId);
  }, [rows, q, chip, sortId]);

  const filterActive = Boolean(q) || chip !== EQUIPE_CHIP.TODOS;

  const chipGroups = useMemo(() => [
    {
      label: 'Mostrar',
      chips: EQUIPE_CHIP_OPTIONS.map((opt) => ({
        id: opt.id,
        label: opt.label,
        selected: chip === opt.id,
        onClick: () => {
          if (opt.id === EQUIPE_CHIP.TODOS) {
            setChip(EQUIPE_CHIP.TODOS);
            return;
          }
          setChip((prev) => (prev === opt.id ? EQUIPE_CHIP.TODOS : opt.id));
        },
      })),
    },
  ], [chip]);

  const columns = useMemo(() => [
    {
      field: 'nome',
      headerName: 'Digitador',
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500} noWrap>
          {params.row.nome || '—'}
        </Typography>
      ),
    },
    {
      field: 'mtbt',
      headerName: 'Inspeção MT/BT',
      minWidth: 260,
      flex: 1.5,
      sortable: false,
      renderHeader: () => (
        <Stack spacing={0}>
          <Typography variant="caption" fontWeight={600}>Inspeção MT/BT</Typography>
          <Typography variant="caption" color="text.secondary">meta {META_DIARIA[LENTE_IDS.MT_BT]}/dia</Typography>
        </Stack>
      ),
      renderCell: (params) => (
        <LenteGlanceCell
          lente={params.row.mtbt}
          lenteId={LENTE_IDS.MT_BT}
          showAdere
        />
      ),
    },
    {
      field: 'usoMutuo',
      headerName: 'Uso mútuo',
      minWidth: 240,
      flex: 1.4,
      sortable: false,
      renderHeader: () => (
        <Stack spacing={0}>
          <Typography variant="caption" fontWeight={600}>Uso mútuo</Typography>
          <Typography variant="caption" color="text.secondary">meta {META_DIARIA[LENTE_IDS.USO_MUTUO]}/dia</Typography>
        </Stack>
      ),
      renderCell: (params) => (
        <LenteGlanceCell
          lente={params.row.usoMutuo}
          lenteId={LENTE_IDS.USO_MUTUO}
          showAdere
        />
      ),
    },
    {
      field: 'acao',
      headerName: 'Ação',
      width: 148,
      minWidth: 148,
      maxWidth: 148,
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <Button
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => openDigitadorDrill({
            digitadorId: params.row.digitadorId,
            nome: params.row.nome,
          })}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Visualizar
        </Button>
      ),
    },
  ], []);

  return (
    <WidgetV2
      title="Todos os digitadores"
      initialExpanded
      actions={[
        {
          isComponent: true,
          component: (
            <DigitacaoSortMenuButton
              sortMode={sortId}
              onChange={setSortId}
              options={EQUIPE_SORT_OPTIONS}
            />
          ),
        },
      ]}
    >
      <Box sx={{ px: 2, pt: 1.5 }}>
        <FilterChipBarV2
          searchLabel="Digitador"
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Nome do digitador"
          chipGroups={chipGroups}
          chipGroupsLayout="search-row"
          filterActive={filterActive}
          filteredCount={filtered.length}
          totalCount={rows.length}
          countLabel="digitadores"
        />
      </Box>
      <TableV2
        rows={filtered}
        loading={loading}
        columns={columns}
        autoHeight
        disablePagination
        rowHeight={88}
        emptyMessage="Nenhum digitador no filtro"
        gridSx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            py: 0.75,
            whiteSpace: 'normal',
            lineHeight: 1.3,
          },
          '& .MuiDataGrid-cell[data-field="acao"]': {
            overflow: 'visible',
            textOverflow: 'clip',
            alignItems: 'center',
            py: 0,
          },
        }}
      />
    </WidgetV2>
  );
};

EquipeTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

EquipeTable.defaultProps = {
  rows: [],
  loading: false,
};

export default EquipeTable;
